import _ from 'lodash';
import classNames from 'classnames';
import React, { FunctionComponent, useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext, Draggable, DragStart, Droppable, DropResult } from 'react-beautiful-dnd';
import { Button, ButtonGroup, Col, Dropdown, FormCheck, Modal, Nav, Row, Tabs, Tab } from 'react-bootstrap';
import { ClipLoader } from 'react-spinners';
import { v4 as uuidv4 } from 'uuid';
import toastr from '../../../toastr';

import modal from '../../typings/Modal';

import CustomerLookup from '../lookups/CustomerLookup';
import Label from '../Label';
import Model from '../../model/ShopKeeper.model';
import VendorLookup from '../lookups/VendorLookup';
import { IVendorSuggestions } from '../../typings/VendorSuggestions';
import {
  ICandidatePartDetail,
  IDerivedVendorSuggestion,
  IPODetail,
  IPOLocation,
  IPOPart,
  IStandalonePOSubmission,
  FinalizedPOPart,
} from '../../typings/Backorder';

/* Derived from BackorderModal.  Enough deviation exists to warrant separate implementation.
   This supports PRO1/SMM3 (sublet/unpriced) & PSO3 standalone POs that bypass the backorder system.

   sufficientQty is a carry over from the back order system, where it's used to determine if a line
   item should be a shown candidate (when false, indicating you need to order it). In this derivation,
   different rules are used to show various line items, so sufficientQty is set to false for all but
   comment lines, and the "Show All" option in the UI from the backordering system becomes the
   "Show Comments" option in this case.
*/

interface IProps {
  container: Object;
  hide: () => void;
  model: Model;
  show: boolean;
}

/* Bootstrap's modal system confuses react-beautiful-dnd positioning calculation
   when dragging. To dance around that problem, we'll use a react portal appended
   directly to the end of the body.  By doing so, the dragging fixed position
   calculation will work correctly in reference to the viewport coordinates.
*/
const portal = document.createElement('div');
portal.classList.add('react-beautiful-dnd-portal-sublets');
document.body.appendChild(portal);

// ck1979 represents a "not set" value (and is also an attribution to the orignal author ;))
const blank = 'ck1979';

const poTemplate: IPODetail = {
  dealerPO: 'NEW',
  dealershipInternalComments: '',
  directShipCustomerNum: '',
  directShipOtherAddress: '',
  directShipOtherAttention: '',
  directShipOtherCityStateZip: '',
  directShipOtherName: '',
  directShipOtherPhone: '',
  directShipType: 'D',
  directShipVendorNum: '',
  freightOnBoard: blank,
  freightPayment: blank,
  miscAddress: '',
  miscAttention: '',
  miscCityStateZip: '',
  miscPhone: '',
  miscVendorName: '',
  shipIn: blank,
  subletType: 'S',
  suggestedVendor: blank,
  vendorSearch: '',
};

const orderTemplate: {
  // Future Use: misc details related to all purchase orders here

  purchaseOrders: Array<{ parts: IPOPart[]; poDetail: IPODetail }>;
} = {
  purchaseOrders: [
    {
      parts: [],
      poDetail: _.clone(poTemplate),
    },
  ],
};

// Reasonably assume everything until the first : is the vendor number.
// This enables showing the vendor name in the auto-suggest box seamlessly.
function extractVendorNum(value: string): string {
  const colonPos = value.indexOf(':');
  return ~colonPos ? value.substr(0, colonPos) : value;
}

// This inits a map which tracks which parts are currently "selected" pending on
// a multi-part drag.  The map will be often reset.  No need to make this a closure.
function initPartSelectionToggleMap(partList: string[]): { [id: string]: boolean } {
  const map = {};
  partList.forEach(id => (map[id] = false));
  return map;
}

const SubletUnpricedPOModal: FunctionComponent<IProps> = ({ container, hide, model, show }) => {
  const isPSO3 = ['PSO3'].includes(model.runMode);
  const isSMM3 = ['SMM3'].includes(model.runMode);

  const poType = isPSO3 ? '' : model.subletOrUnpricedItemPendingIssue;

  // Only available on PRO1/SMM3 if we have sublets as candidates and this client has a body shop
  const showServiceOrBodyOption = isPSO3
    ? false
    : model.sysParams.bodyShop && model.subletOrUnpricedItemPendingIssue !== 'Unpriced';

  /**** STATE HOOKS ****/

  // Parts will only be extracted from the model once.  No need to use a memo hook.
  // The BO modal passes around partIDs only for performance, which is an index into
  // the resultant (static) parts state derived here.
  const [parts] = useState<ICandidatePartDetail[]>(() => {
    const extractedParts: ICandidatePartDetail[] = [];
    let nextIndex = 0;

    model.lineItems.forEach((lineItem, invoiceIndex) => {
      // Do not allow line items previously added from confirmations nor previously issued to a repair order.
      if (!lineItem.fetched || lineItem.confirmationSale || lineItem.confirmationComment || lineItem.issuedToRO) {
        return;
      }

      // *This includes faux-sublets*, which are non-SUBL/SUBP default parts with the PO flag set in ATB3.
      // In the case of faux-sublets, subletComment will not be required nor shown/submitted within this component.
      const isSubletOrATB3PODefaultPart = model.subletCandidates.includes(lineItem.defaultPartNum!);

      // PSO3 allows standard parts, non-tracked parts, and sublet/faux-sublet parts.
      // Unpriced parts aren't a thing on PSO3.
      if (isPSO3) {
        if (lineItem.supercedenceDetails && lineItem.supercedenceDetailsSupplemental && lineItem.detailSupplemental) {
          // 1:1 and 1:1:n supercedence chains supported, splits forthcoming
          if (lineItem.supercedenceDetails.length > 1) {
            console.error(`${lineItem.partNum} split supercedence not yet supported on stand alone purchase orders.`);
            return;
          }

          const supercedenceDetail = lineItem.supercedenceDetails[0];
          const supercedenceDetailSupplemental = _.get(
            lineItem.supercedenceDetailsSupplemental,
            `${supercedenceDetail.partNum}`
          );

          const req = supercedenceDetail.quantity; // this also accounts for 1:n quantity escalation, previously calculated on inquiry
          const oh = supercedenceDetail.priceSymbol.endsWith('3')
            ? req
            : Number.parseInt(_.get(supercedenceDetailSupplemental, 'onHand', ''), 10) || 0;
          const supercedenceEscalationFactor = supercedenceDetail.quantity / lineItem.quantity; // this will be a whole number by deduction

          extractedParts[nextIndex++] = {
            desc: supercedenceDetail.shortDescription
              ? supercedenceDetail.shortDescription
              : supercedenceDetail.longDescription,
            invoiceIndex,
            ns: false,
            oh,
            partNum: supercedenceDetail.partNum,
            req,
            stockClass: supercedenceDetail.stockClass,
            sufficientQty: false,
            supercedenceEscalationFactor,
            // price forces do not exist on supercedence parts
          };

          // Regular stocking part?
        } else if (lineItem.detail) {
          const oh = lineItem.detail.priceSymbol.endsWith('3')
            ? lineItem.quantity
            : lineItem.detailSupplemental
            ? Number.parseInt(lineItem.detailSupplemental.onHand, 10) || 0
            : 0;
          const req = lineItem.quantity;

          extractedParts[nextIndex++] = {
            desc: lineItem.forcedDescription
              ? lineItem.forcedDescription
              : lineItem.detail.shortDescription
              ? lineItem.detail.shortDescription
              : lineItem.detail.longDescription,
            invoiceIndex,
            listPrice: lineItem.forcedListPrice, // Force prices are undefined if they don't exist
            netPrice: lineItem.forcedNetPrice,
            ns: false,
            oh,
            partNum: lineItem.partNum,
            req,
            stockClass: lineItem.detail.stockClass,
            sufficientQty: false,
            tradePrice: lineItem.forcedTradePrice,
          };

          // Non-Stocking Part
        } else if (lineItem.nonStockingPartNum) {
          extractedParts[nextIndex++] = {
            desc: lineItem.nonStockingPartDescription!,
            invoiceIndex,
            listPrice: lineItem.nonStockingPartListPrice,
            netPrice: lineItem.nonStockingPartNetPrice,
            ns: true,
            oh: 0,
            partNum: lineItem.nonStockingPartNum,
            priceSymbol: lineItem.priceSymbol,
            req: lineItem.quantity,
            stockClass: lineItem.stockClass!,
            sufficientQty: false,
            tradePrice: lineItem.nonStockingPartTradePrice,
          };

          // Sublet or ATB3 faux-sublet (PO option checked)
        } else if (isSubletOrATB3PODefaultPart) {
          extractedParts[nextIndex++] = {
            desc: lineItem.defaultPartDescription!,
            invoiceIndex,
            listPrice: lineItem.defaultPartListPrice,
            netPrice: lineItem.defaultPartNetPrice,
            ns: true,
            oh: 0,
            partNum: lineItem.defaultPartNum!,
            priceSymbol: lineItem.priceSymbol,
            req: lineItem.quantity,
            stockClass: lineItem.stockClass!,
            subletComment: lineItem.defaultPartSubletComment,
            sufficientQty: false,
            tradePrice: lineItem.defaultPartTradePrice,
          };

          // Invoice comment/memo
        } else if (lineItem.comment) {
          /* The main invoice model assigns a partNum of "LINEITEM_COMMENT" to comments/memos.
             Within this module you will also see "PO_COMMENT" which refers to comments added with
             the PO Comment entry box on the modal itself.  The difference is the former is linked
             to a candidate position index while the latter is not.
          */
          extractedParts[nextIndex++] = {
            desc: lineItem.comment,
            invoiceIndex,
            ns: false,
            oh: 0,
            partNum: 'LINEITEM_COMMENT',
            req: 0,
            stockClass: '',
            sufficientQty: true,
          };
        }

        // PRO1/SMM3 allows unpriced parts, and sublet/faux-sublet parts.
        // Stocking parts and priced non-tracked parts use the Backorder/Confirmation system.
      } else {
        // Allow only unpriced, SUBL/SUBP/faux-sub parts, and comments.
        if (!(lineItem.unpriced || isSubletOrATB3PODefaultPart || lineItem.comment)) {
          return;
        }

        // Do not allow unpriced default parts unless it's a SUBL/SUBP/faux-sub
        if (lineItem.defaultPartNum && !isSubletOrATB3PODefaultPart) {
          return;
        }

        if (isSubletOrATB3PODefaultPart) {
          extractedParts[nextIndex++] = {
            desc: lineItem.defaultPartDescription!,
            invoiceIndex,
            listPrice: lineItem.defaultPartListPrice,
            netPrice: lineItem.defaultPartNetPrice,
            ns: true,
            oh: 0,
            operationNumber: lineItem.roDetail!.operationNumber,
            partNum: lineItem.defaultPartNum!,
            priceSymbol: lineItem.priceSymbol,
            req: lineItem.quantity,
            stockClass: lineItem.stockClass!,
            subletComment: lineItem.defaultPartSubletComment,
            sufficientQty: false,
            tradePrice: lineItem.defaultPartTradePrice,
            unpriced: lineItem.unpriced, // e.g. $SUBL or $SUBP
          };
        } else if (lineItem.unpriced) {
          extractedParts[nextIndex++] = {
            desc: lineItem.nonStockingPartDescription!,
            invoiceIndex,
            listPrice: lineItem.nonStockingPartListPrice,
            netPrice: lineItem.nonStockingPartNetPrice,
            ns: true,
            oh: 0,
            operationNumber: lineItem.roDetail!.operationNumber,
            partNum: lineItem.nonStockingPartNum!,
            priceSymbol: lineItem.priceSymbol,
            req: lineItem.quantity,
            stockClass: lineItem.stockClass!,
            sufficientQty: false,
            tradePrice: lineItem.nonStockingPartTradePrice,
            unpriced: true,
          };

          // Invoice comment/memo
        } else if (lineItem.comment) {
          /* The main invoice model assigns a partNum of "LINEITEM_COMMENT" to comments/memos.
           Within this module you will also see "PO_COMMENT" which refers to comments added with
           the PO Comment entry box on the modal itself.  The difference is the former is linked
           to a candidate position index while the latter is not.
        */
          extractedParts[nextIndex++] = {
            desc: lineItem.comment,
            invoiceIndex,
            ns: false,
            oh: 0,
            operationNumber: lineItem.roDetail!.operationNumber,
            partNum: 'LINEITEM_COMMENT',
            req: 0,
            stockClass: '',
            sufficientQty: true,
          };
        }
      }
    });

    return extractedParts;
  });

  // The PO builder does all state tracking with IDs only with eventual reference to the parts data structure.
  // This calculation is used in multiple places, so no reason to recalc repeatedly since the extracted parts won't change.
  const [partIndexes] = useState(Object.keys(parts));

  const [defaultPO] = useState(() => {
    const newDefaultPO = _.clone(poTemplate);
    newDefaultPO.directShipCustomerNum = model.activeCustomerID || '';
    return newDefaultPO;
  });

  const [order, setOrder] = useState(() => {
    // Seed the confirmation detail controls from the model
    const newOrder = _.cloneDeep(orderTemplate);
    newOrder.purchaseOrders[0].poDetail = _.clone(defaultPO);
    return newOrder;
  });

  // react-beautiful-dnd doesn't track drag state the precise way we require
  const [dragState, setDragState] = useState({ isDragging: false, id: '0' });

  // This hook tracks the selected state of candidate parts (multi-select)
  const [partSelectionToggles, setPartSelectionToggles] = useState(initPartSelectionToggleMap(partIndexes));
  const selectedPartCount = Object.values(partSelectionToggles).filter(x => x).length;

  const [activePO, setActivePO] = useState(0); // track active PO tab
  const [candidatePartList, setCandidatePartList] = useState(partIndexes);

  const [isProcessing, setIsProcessing] = useState(false);

  // eslint-disable-next-line
  let [showAllParts, setShowAllParts] = useState(false);

  const [miscVendorNums, setMiscVendorNums] = useState([] as string[]);
  const [poCommentLineIsEditing, setPoCommentLineIsEditing] = useState(false);

  // Note: a missing suggestion for a particular stock class indicates the stock class is invalid
  //       an empty array for a particular stock class indicates no default vendors were set for that stock class
  const [vendorSuggestions, setVendorSuggestions] = useState<IVendorSuggestions>({ ATB13MISC: [] });

  // These are the caluclated vendor suggestions per PO (indexed on PO #) based on aggregate stock classes on the PO.
  const [derivedVendorSuggestions, setDerivedVendorSuggestions] = useState<IDerivedVendorSuggestion[]>([]);

  // We need arrays of refs which can dynmically grow - thus using "old school" refs rather than useRef hooks
  const [directShipCustomerLookupRefs] = useState<CustomerLookup[]>([]);
  const [directShipVendorLookupRefs] = useState<VendorLookup[]>([]);
  const [vendorSearchLookupRefs] = useState<VendorLookup[]>([]);

  // Static refs not tied to dynamic POs
  const poCommentInputRef = useRef<HTMLInputElement>(null); // Main comment input
  const poCommentEditInputRef = useRef<HTMLInputElement>(null); // Dynamic per PO edit-mode line

  /**** END STATE HOOKS ****/

  /**** MEMO HOOKS ****/

  // This memo hook prevents the need to re-render (and re-order) the derived suggested vendors on each render,
  // which could be costly since we're using controlled inputs and this would occur on every keystroke.
  const renderedDerivedVendorSuggestions = useMemo(() => {
    const maxIndex = order.purchaseOrders.length;
    const result = [];

    for (let poIndex = 0; poIndex < maxIndex; poIndex++) {
      if (derivedVendorSuggestions[poIndex]) {
        result[poIndex] = Object.keys(derivedVendorSuggestions[poIndex])
          .sort(
            (a, b) =>
              derivedVendorSuggestions[poIndex][b].stockClasses.size -
              derivedVendorSuggestions[poIndex][a].stockClasses.size
          )
          .map((vendorNum, vKey) => {
            const vDetails = derivedVendorSuggestions[poIndex][vendorNum];
            return (
              <option
                value={vendorNum}
                key={vKey}
                title={`${vDetails.state} ${vDetails.city} ${vDetails.phone}`.trim()}
              >
                {`${vendorNum}: ${vDetails.name}`}
              </option>
            );
          });
      }
    }

    return result;
  }, [derivedVendorSuggestions]);

  // A fast transposition map to deal with the sequential render index demands of rb-dnd v12+
  // [renderIndex]: candidatePartListIndex
  const candidatePartListTranspositionMap = useMemo(() => {
    const map = [];
    let nextIndex = 0;
    for (let i = 0; i < candidatePartList.length; i++) {
      if (showAllParts) {
        map[nextIndex++] = i;
      } else {
        if (!(parts[candidatePartList[i]] as ICandidatePartDetail).sufficientQty) {
          map[nextIndex++] = i;
        }
      }
    }
    return map;
  }, [candidatePartList, showAllParts]);

  // Perf feature for calculating the visible candidate length (based on "Show All" flag)
  const visibleCandidatePartListLength: number = useMemo(
    () =>
      candidatePartList.reduce((count: number, partIndex: string) => {
        const sufficientQty = (parts[partIndex] as ICandidatePartDetail).sufficientQty;
        return !sufficientQty || (sufficientQty && showAllParts) ? count + 1 : count;
      }, 0),
    [candidatePartList, showAllParts]
  );

  /**** END MEMO HOOKS ****/

  /**** EFFECT HOOKS ****/

  // Fetch all possible vendor suggestions in 1 shot on mount
  useEffect(() => {
    const fetchVendorSuggestions = async () => {
      const result = await model.getVendorSuggestions(parts.filter(x => x.stockClass !== '').map(y => y.stockClass));
      setVendorSuggestions(result);

      const mvNums = [] as string[];
      result.ATB13MISC.forEach(mv => mvNums.push(mv.num));
      setMiscVendorNums(mvNums);

      if (model.logging) {
        console.warn('Vendor Suggestions / Misc Vendor Nums');
        console.log(result);
        console.log(mvNums);
      }
    };
    fetchVendorSuggestions();
  }, []);

  // Hydrate-Rehydrate the react-autosuggest controls when necessary.
  // Wait briefly for RBS to possibly render / remove new tabs.
  useEffect(() => {
    setTimeout(() => {
      order.purchaseOrders.forEach((purchaseOrder, index) => {
        directShipCustomerLookupRefs[index].value = purchaseOrder.poDetail.directShipCustomerNum;
        directShipVendorLookupRefs[index].value = purchaseOrder.poDetail.directShipVendorNum;
        vendorSearchLookupRefs[index].value = purchaseOrder.poDetail.vendorSearch;
      });
    }, 150);
  }, [order]);

  // Only run this hook when parts are added to / removed from a purchase order
  // Note: this requires thoughtful ref handling when cloning the bo object.
  useEffect(() => {
    const newDerivedVendorSuggestions: IDerivedVendorSuggestion[] = [];

    order.purchaseOrders.forEach((po, poIndex) => {
      const activeStockClassesThisPO = new Set<string>();
      const vendorStockClassMatches = {} as IDerivedVendorSuggestion;

      // Derive unique set of active stock classes on this PO
      po.parts.forEach(part => {
        if ('poComment' in part) return;

        const partDetails = parts[part.partIndex] as ICandidatePartDetail;
        if (partDetails.stockClass !== '') {
          activeStockClassesThisPO.add(partDetails.stockClass);
        }
      });

      activeStockClassesThisPO.forEach(stockClass => {
        if (vendorSuggestions[stockClass]) {
          vendorSuggestions[stockClass].forEach(vendorDetails => {
            if (vendorStockClassMatches[vendorDetails.num]) {
              vendorStockClassMatches[vendorDetails.num].stockClasses.add(stockClass);
            } else {
              vendorStockClassMatches[vendorDetails.num] = {
                city: vendorDetails.city,
                name: vendorDetails.name,
                phone: vendorDetails.phone,
                state: vendorDetails.state,
                stockClasses: new Set([stockClass]),
              };
            }
          });
        }
      });

      newDerivedVendorSuggestions[poIndex] = vendorStockClassMatches;
    });

    setDerivedVendorSuggestions(newDerivedVendorSuggestions);

    /* There's a possibility a user just deleted a part and its associated selected vendor suggestion.
       If so, clear the previously suggested value from the order state model as well.  This would
       only occur on the "active" PO.
    */
    const activeSuggestedVendor = order.purchaseOrders[activePO].poDetail.suggestedVendor;
    if (
      activeSuggestedVendor !== blank &&
      !miscVendorNums.includes(activeSuggestedVendor) &&
      !(activeSuggestedVendor in newDerivedVendorSuggestions[activePO])
    ) {
      const newOrder = _.clone(order);
      newOrder.purchaseOrders[activePO].poDetail.suggestedVendor = blank;
      setOrder(newOrder);
    }
  }, [order.purchaseOrders]);

  // This hook auto-selects the PO comment line text so it's fast to replace
  useEffect(() => {
    if (poCommentLineIsEditing) {
      const el = poCommentEditInputRef.current!;
      el.focus();
      el.setSelectionRange(0, el.value.length);
    }
  }, [poCommentLineIsEditing]);

  /**** END EFFECT HOOKS ****/

  /**** EVENT HANDLING & UTILITY METHODS ****/

  // User wants to shortcut putting all candidate parts on active PO (very common)
  const addAllPartsToPO = async () => {
    if (isProcessing) {
      return;
    }

    const newOrder = _.cloneDeep(order); // need fresh PO ref for suggested vendors hook
    const activePOParts = newOrder.purchaseOrders[activePO].parts;
    const hiddenParts: string[] = []; // used to keep "sufficientQty" parts on the candidate list (hidden)

    let includeComments = false;

    // Figure out if we should include comments in "Show All Parts" mode before moving anything (if they exist)
    if (
      showAllParts &&
      candidatePartList.some(partIndex => (parts[partIndex] as ICandidatePartDetail).partNum === 'LINEITEM_COMMENT')
    ) {
      includeComments = await new Promise(resolve => {
        modal.confirm({
          title: 'Comments',
          message: 'Include comments / memos?',
          confirmLabels: ['Yes', 'No'],
          callback: () => resolve(true),
          onClose: () => resolve(false),
        });
      });
    }

    candidatePartList.forEach(partIndex => {
      const partDetails = parts[partIndex] as ICandidatePartDetail;

      if (partDetails.sufficientQty && !showAllParts) {
        // keep all parts of sufficient qty and comments in the "hidden" list
        // in case "show all parts" is eventually checked
        hiddenParts.push(partIndex);

        // otherwise move all parts (including "hidden" ones), unless comments are suppressed
      } else {
        if (!includeComments && partDetails.partNum === 'LINEITEM_COMMENT') {
          hiddenParts.push(partIndex);
        } else {
          activePOParts.push(createPOPart(partIndex));
        }
      }
    });

    setOrder(newOrder);
    setPartSelectionToggles(initPartSelectionToggleMap(partIndexes));
    setCandidatePartList(hiddenParts);
  };

  // User wishes to attach an additional PO to the confirmation
  const addPO = () => {
    if (isProcessing) {
      return;
    }

    const newOrder = _.clone(order);
    newOrder.purchaseOrders.push({
      parts: [],
      poDetail: _.clone(defaultPO),
    });
    setOrder(newOrder);
  };

  // Used by the PO comment entry box, not by comments tracked from the model
  const addPOComment = () => {
    if (isProcessing) {
      return;
    }

    const newOrder = _.clone(order);
    newOrder.purchaseOrders[activePO].parts.push({
      boAction: 'ORDER_FULL_QUANTITY', // IGNORED
      partIndex: `PO_COMMENT_${uuidv4()}`,
      poComment: poCommentInputRef.current!.value.trim(),
      poCommentIsEditing: false,
      prebill: false, // IGNORED
    });

    poCommentInputRef.current!.value = '';
    setOrder(newOrder);
  };

  // User has selected multiple parts for a multipart drag, but wants to clear selection
  const clearPartSelection = () => {
    setPartSelectionToggles(initPartSelectionToggleMap(partIndexes));
  };

  // Given a partIndex, return a data structure suitable for PO placement
  const createPOPart = (partIndex: string): IPOPart => {
    const partDetails = parts[partIndex] as ICandidatePartDetail;
    return partDetails.partNum === 'LINEITEM_COMMENT'
      ? {
          boAction: 'ORDER_FULL_QUANTITY', // IGNORED
          partIndex,
          poComment: partDetails.desc,
          poCommentIsEditing: false,
          prebill: false, // IGNORED
        }
      : {
          boAction: 'ORDER_FULL_QUANTITY',
          partIndex,
          prebill: false,
        };
  };

  // Return all parts on the active PO to the candidate part list, then delete the PO
  // The manual CSS toggling to the panes simulates the manual tabbing animation the user expects.
  const deletePO = () => {
    if (isProcessing) {
      return;
    }

    const newPartList = _.clone(candidatePartList);
    const activePOParts = order.purchaseOrders[activePO].parts;
    activePOParts.forEach(part => {
      // Skip comment lines
      if (!('poComment' in part)) {
        newPartList.push(part.partIndex);
      }
    });
    setCandidatePartList(newPartList);

    const newOrder = _.cloneDeep(order); // need fresh PO refs for suggested vendors hook
    if (newOrder.purchaseOrders.length === 1) {
      newOrder.purchaseOrders[0] = {
        parts: [],
        poDetail: _.clone(defaultPO),
      };
    } else {
      newOrder.purchaseOrders.splice(activePO, 1);
    }
    setOrder(newOrder);

    const flashPO = (po: number) => {
      document.getElementById(`sk-poNavigator-tabpane-po-${po}`)!.classList.remove('active', 'show');
      setTimeout(() => {
        document.getElementById(`sk-poNavigator-tabpane-po-${po}`)!.classList.add('active');
        setTimeout(() => {
          document.getElementById(`sk-poNavigator-tabpane-po-${po}`)!.classList.add('show');
        }, 120);
      }, 120);
    };

    // Last PO in attachment list deleted?
    if (activePO === newOrder.purchaseOrders.length) {
      setActivePO(activePO - 1);
      setTimeout(() => {
        flashPO(activePO - 1);
      }, 25);
    } else {
      flashPO(activePO);
    }
  };

  // When dropping over a PO, we want the effect of the part dropping in the PO part list area
  // This only effects the first part dropped.
  const getDraggableStyle = (style: any, snapshot: any, poEmpty: boolean) => {
    if (snapshot.isDropAnimating && snapshot.draggingOver && snapshot.draggingOver.startsWith('po-') && poEmpty) {
      const { moveTo } = snapshot.dropAnimation;
      // move to the right spot
      const translate = `translate(${moveTo.x + 15}px, ${moveTo.y + 200}px)`;
      return {
        ...style,
        transform: `${translate}`,
      };
    } else {
      return style;
    }
  };

  const handleShowAllParts = (show: boolean) => {
    showAllParts = show; // this cycle (scripted shorcuts)
    setShowAllParts(show); // next cycle
    clearPartSelection();
  };

  // If vendor search or suggested vendor is set to a misc vendor, return true.  The former dominates.
  const isMiscVendorSelected = () => {
    const activeSuggestedVendor = order.purchaseOrders[activePO].poDetail.suggestedVendor;
    const activeVendorSearch = extractVendorNum(order.purchaseOrders[activePO].poDetail.vendorSearch.trim());
    const activeVendor = activeVendorSearch || activeSuggestedVendor;

    return miscVendorNums.includes(activeVendor);
  };

  const onDragEnd = (result: DropResult) => {
    setDragState({ isDragging: false, id: '0' });

    // Not on a droppable
    if (!result.destination || isProcessing) return;

    /* react-beautiful-dnd requires sequential indexes for proper operation, and thus only works upon
       "shown" indexes.  This creates a problem if "show parts" isn't checked, because now the indexes
       within the candidate part list are not 1:1 with the "indexes" of the draggables in the list.
       Thus, we have to transpose when doing our splices when showAllParts is off (the transposed values
       are also correct when showAllParts is on, it's a simple 1:1 mapping).  We only use transposition
       mapping when referring to candidate parts (i.e. po line movement can rely direclty on rdnd indexes).
    */

    // Move item within candidate part list
    if (
      result.source.droppableId === 'partList' &&
      result.destination.droppableId === 'partList' &&
      result.source.index !== result.destination.index
    ) {
      if (selectedPartCount > 1) {
        // TODO: support for multi part drag within the candidate list - not that important
      } else {
        const newPartList = _.clone(candidatePartList);
        newPartList.splice(
          candidatePartListTranspositionMap[result.destination.index],
          0,
          newPartList.splice(candidatePartListTranspositionMap[result.source.index], 1)[0]
        );
        setCandidatePartList(newPartList);
      }
      return;
    }

    // Place item on an attached PO
    if (result.source.droppableId === 'partList' && result.destination.droppableId.startsWith('po-')) {
      // Add part to the PO tab the user dropped on
      const newOrder = _.cloneDeep(order); // need fresh PO ref for suggested vendors hook
      const poID = parseInt(result.destination.droppableId.slice(3), 10) || 0;
      const insertIndex = result.destination.index;
      let newPartList;

      // Multi-part Drag
      if (selectedPartCount > 1) {
        const poParts: IPOPart[] = [];
        for (const partIndex of Object.keys(partSelectionToggles)) {
          if (partSelectionToggles[partIndex]) {
            poParts.push(createPOPart(partIndex));
          }
        }
        newOrder.purchaseOrders[poID].parts.splice(insertIndex, 0, ...poParts);
        newPartList = candidatePartList.filter(id => !partSelectionToggles[id]);

        // Single Part Drag
      } else {
        newOrder.purchaseOrders[poID].parts.splice(insertIndex, 0, createPOPart(result.draggableId));
        newPartList = candidatePartList.filter(id => id !== result.draggableId);
      }

      setOrder(newOrder);
      setPartSelectionToggles(initPartSelectionToggleMap(partIndexes));
      setCandidatePartList(newPartList);

      return;
    }

    // Move line within PO
    if (
      result.source.droppableId.startsWith('po-') &&
      result.destination.droppableId.startsWith('po-') &&
      result.source.index !== result.destination.index
    ) {
      const newOrder = _.clone(order);
      const lines = newOrder.purchaseOrders[activePO].parts; // PO Part lines

      lines.splice(result.destination.index, 0, lines.splice(result.source.index, 1)[0]);

      setOrder(newOrder);
      return;
    }
  };

  const onBeforeDragStart = (start: DragStart) => {
    setDragState({ isDragging: true, id: start.draggableId });

    // If the user ends up dragging a part which wasn't part of a previously set
    // multi-selection group, clear out the selection group.
    if (selectedPartCount > 1 && !partSelectionToggles[start.draggableId]) {
      setPartSelectionToggles(initPartSelectionToggleMap(partIndexes));
    }
  };

  // This is used for multi part selection / deselection.
  // It is not called when a user does a long click + drag.
  const onPartClick = (event: React.MouseEvent, id: string) => {
    // Prob will never use this, but it's stubbed out in case people ask for it.
    if (event.shiftKey) {
      // Select all parts between first & last items clicked
    }
    if (event.ctrlKey) {
      // Multi select / does not clear other items selected
    }

    const newSelectedParts = _.clone(partSelectionToggles);
    newSelectedParts[id] = !newSelectedParts[id];
    setPartSelectionToggles(newSelectedParts);
  };

  // Track the active tab for actions such as adding all candidate parts, delete PO, etc.
  // Per RBS4 type declarations, eventKey is string | null (not sure what condition would cause null)
  const onPOTabSelect = (eventKey: string | null) => {
    if (eventKey) {
      setActivePO(parseInt(eventKey.slice(3), 10));
    }
  };

  // Process the Purchase Orders
  const process = async () => {
    // Here we link/merge the candidate part details into the finalized order submission data structure
    const orderSubmission: IStandalonePOSubmission = {
      customerID: model.activeCustomerID!,
      ppc: model.ppcParams.ppc,
      purchaseOrders: [], // "Finalized" purchase orders, linked to candidate part details
      roNum: model.activeRONumber || '', // only used by PRO1/SMM3
    };

    order.purchaseOrders.forEach(po => {
      // Note some of the "finalized" properties are extracted from the PO part line data structure,
      // while others come from the candidate order parts data structure.
      const finalizedPOParts: FinalizedPOPart[] = [];
      po.parts.forEach(part => {
        let finalizedPOPart: FinalizedPOPart;

        if ('poComment' in part) {
          // PO only comment which should not be issued to the RO
          if (part.partIndex.startsWith('PO_COMMENT')) {
            finalizedPOPart = {
              poComment: part.poComment!,
              operationNumber: 0,
            };
          } else {
            const partDetails = parts[part.partIndex] as ICandidatePartDetail;
            finalizedPOPart = {
              poComment: part.poComment!,
              operationNumber: partDetails.operationNumber,
            };
          }
        } else {
          const partDetails = parts[part.partIndex] as ICandidatePartDetail;
          const partNum = model.defaultParts.has(partDetails.partNum)
            ? `X:${partDetails.partNum}`
            : partDetails.partNum;

          // Standalone POs disregard boAction, ns, oh, prebill, and supercedenceEscalationFactor
          // Our type system here is shared with the Backorder process, so it's simpler to leave these extraneous.
          finalizedPOPart = {
            boAction: part.boAction,
            desc: partDetails.desc,
            invoiceIndex: partDetails.invoiceIndex,
            listPrice: partDetails.listPrice,
            netPrice: partDetails.netPrice,
            ns: partDetails.ns,
            oh: partDetails.oh,
            operationNumber: partDetails.operationNumber,
            partNum,
            prebill: part.prebill,
            priceSymbol: partDetails.priceSymbol,
            req: partDetails.req,
            stockClass: partDetails.stockClass,
            tradePrice: partDetails.tradePrice,
            unpriced: partDetails.unpriced,
          };

          if (partDetails.supercedenceEscalationFactor !== 1) {
            finalizedPOPart.supercedenceEscalationFactor = partDetails.supercedenceEscalationFactor;
          }

          if (partDetails.subletComment) {
            finalizedPOPart.subletComment = partDetails.subletComment;
          }
        }

        finalizedPOParts.push(finalizedPOPart);
      });

      orderSubmission.purchaseOrders.push({
        parts: finalizedPOParts,
        poDetail: { ..._.clone(po.poDetail), vendorSearch: extractVendorNum(po.poDetail.vendorSearch) },
      });
    });

    if (model.logging) {
      console.log('Order State / Submission Request:');
      console.log({
        order,
        orderSubmission,
        candidateParts: parts,
      });
    }

    const success = await model.processStandalonePO(orderSubmission, hide);

    if (success) {
      /* On success, processStandalonePO() will hide this modal for us, then pop the printer selector.
         The model will also refresh the RO screen automatically in the background while the user
         handles their printing needs.
      */
    } else {
      setIsProcessing(false);
    }
  };

  // User has clicked the trash icon - remove part from PO and return to candidate part list
  const removePartLine = ({ partLineIndex, poIndex }: IPOLocation) => {
    if (isProcessing) {
      return;
    }

    // Skip "PO Comment" comment lines added from modal directly
    if (!order.purchaseOrders[poIndex].parts[partLineIndex].partIndex.startsWith('PO_COMMENT_')) {
      const newPartList = _.clone(candidatePartList);
      newPartList.push(order.purchaseOrders[poIndex].parts[partLineIndex].partIndex);
      setCandidatePartList(newPartList);
    }

    const newOrder = _.cloneDeep(order); // need fresh PO ref for suggested vendors hook
    newOrder.purchaseOrders[poIndex].parts.splice(partLineIndex, 1);
    setOrder(newOrder);
  };

  // When set, the PO comment line becomes editable
  const setCommentIsEditing = ({ partLineIndex, poIndex }: IPOLocation) => {
    const newOrder = _.clone(order);
    newOrder.purchaseOrders[poIndex].parts[partLineIndex].poCommentIsEditing = true;
    setOrder(newOrder);
    setPoCommentLineIsEditing(true);
  };

  const updatePartLineComment = ({ partLineIndex, poIndex }: IPOLocation) => {
    const newOrder = _.clone(order);
    newOrder.purchaseOrders[poIndex].parts[partLineIndex].poComment = poCommentEditInputRef.current!.value;
    newOrder.purchaseOrders[poIndex].parts[partLineIndex].poCommentIsEditing = false;
    setOrder(newOrder);
    setPoCommentLineIsEditing(false);
  };

  // Called by the onChange handler of every po input field - this should be very fast in the VDOM
  const updatePOField = <T extends keyof IPODetail>(poIndex: number, field: T, value: IPODetail[T]) => {
    if (isProcessing) {
      return;
    }

    const newOrder = _.clone(order);
    newOrder.purchaseOrders[poIndex].poDetail[field] = value;
    setOrder(newOrder);
  };

  /* This is a basic field validator which makes sure all POs are properly filled out.
     We'll eventually enhance this with per field error highlighting, but this will do for now (or maybe forever).
     Short circuits on first failed test.
  */
  const validateOrder = () => {
    const fail = (reason: string, toastId?: string) => {
      if (reason !== '') {
        toastr.error('Error', reason, { toastId });
      }
      return false;
    };

    // Validate the fields of each "attached" PO
    const poMaxLength = order.purchaseOrders.length;
    for (let poIndex = 0; poIndex < poMaxLength; poIndex++) {
      const po = order.purchaseOrders[poIndex];
      let vendorSearch = po.poDetail.vendorSearch.trim();
      let directShipCustomerNum = po.poDetail.directShipCustomerNum.trim();
      let directShipVendorNum = po.poDetail.directShipVendorNum.trim();

      /* React-autosuggest inputs could potentially be out of sync with po state during this render
         validation, so double check the DOM controls directly for updates to the state before validating.
         This occurs because these controls only update the state on selection or blur, not keystroke.
         Blur creates a state change on the _next_ render cycle (via hook), and a direct press of process
         by the user would call this function before the hook updates the state.  Do the DOM check as safely
         as possible.
      */
      let el;
      el = document.getElementById(`vendorSearchLookup${poIndex}`) as HTMLInputElement;
      if (el) {
        const vendorSearchDOM = el.value.trim();
        if (vendorSearchDOM !== vendorSearch) {
          vendorSearch = vendorSearchDOM;
          po.poDetail.vendorSearch = vendorSearch;
        }
      }
      el = document.getElementById(`directShipCustomerLookup${poIndex}`) as HTMLInputElement;
      if (el) {
        const directShipCustomerNumDOM = el.value.trim();
        if (directShipCustomerNumDOM !== directShipCustomerNum) {
          directShipCustomerNum = directShipCustomerNumDOM;
          po.poDetail.directShipCustomerNum = directShipCustomerNum;
        }
      }
      el = document.getElementById(`directShipVendorLookup${poIndex}`) as HTMLInputElement;
      if (el) {
        const directShipVendorNumDOM = el.value.trim();
        if (directShipVendorNumDOM !== directShipVendorNum) {
          directShipVendorNum = directShipVendorNumDOM;
          po.poDetail.directShipVendorNum = directShipVendorNum;
        }
      }

      let miscVendorSelected = false;

      if (po.parts.length === 0) {
        return fail(`PO ${poIndex + 1}: No Parts to Process`);
      }

      const vendorRequired = true;

      if (vendorRequired && po.poDetail.suggestedVendor === blank && vendorSearch === '') {
        return fail(`PO ${poIndex + 1}: No Vendor Selected`);
      }

      // Vendor search dominates suggested vendor
      if (vendorSearch !== '') {
        if (miscVendorNums.includes(extractVendorNum(vendorSearch))) {
          miscVendorSelected = true;
        }
      } else if (miscVendorNums.includes(po.poDetail.suggestedVendor)) {
        miscVendorSelected = true;
      }

      if (miscVendorSelected) {
        if (po.poDetail.miscVendorName.trim() === '') {
          return fail(`PO ${poIndex + 1}: Misc Vendor Name Not Set`);
        }
        if (po.poDetail.miscAddress.trim() === '') {
          return fail(`PO ${poIndex + 1}: Misc Vendor Address Not Set`);
        }
        if (po.poDetail.miscCityStateZip.trim() === '') {
          return fail(`PO ${poIndex + 1}: Misc Vendor City/State/Zip Not Set`);
        }
        if (po.poDetail.miscPhone.trim() === '') {
          return fail(`PO ${poIndex + 1}: Misc Vendor Phone Not Set`);
        } else if (!validatePhoneField(po.poDetail.miscPhone)) {
          return fail(`PO ${poIndex + 1}: Misc vendor phone requires 10 digits.`, `PO${poIndex}MiscVendorPhone`);
        }
      }

      if (po.poDetail.directShipType === 'C' && directShipCustomerNum === '') {
        return fail(`PO ${poIndex + 1}: Direct Ship Customer # Not Set`);
      }
      if (po.poDetail.directShipType === 'V' && directShipVendorNum === '') {
        return fail(`PO ${poIndex + 1}: Direct Ship Vendor # Not Set`);
      }

      if (po.poDetail.directShipType === 'O') {
        if (po.poDetail.directShipOtherName.trim() === '') {
          return fail(`PO ${poIndex + 1}: Direct Ship Other Name Not Set`);
        }
        if (po.poDetail.directShipOtherAttention.trim() === '') {
          return fail(`PO ${poIndex + 1}: Direct Ship Other Attention Not Set`);
        }
        if (po.poDetail.directShipOtherAddress.trim() === '') {
          return fail(`PO ${poIndex + 1}: Direct Ship Other Address Not Set`);
        }
        if (po.poDetail.directShipOtherCityStateZip.trim() === '') {
          return fail(`PO ${poIndex + 1}: Direct Ship Other City/State/Zip Not Set`);
        }
        if (po.poDetail.directShipOtherPhone.trim() === '') {
          return fail(`PO ${poIndex + 1}: Direct Ship Other Phone Not Set`);
        } else if (!validatePhoneField(po.poDetail.directShipOtherPhone)) {
          return fail(
            `PO ${poIndex + 1}: Direct ship to "other" phone requires 10 digits.`,
            `PO${poIndex}DirectShipPhone`
          );
        }
      }

      // Remove shipIn requirement on standalone POs per Vic 9/16/21
      // if (po.poDetail.shipIn === blank) {
      //   return fail(`PO ${poIndex + 1}: Ship In Required`);
      // }
    }

    return true;
  };

  // A valid phone number is any string which contains 10 digits, so (702) 530-2199 or 7025302199, for example.
  const validatePhoneField = (value: string) => {
    const containsTenDigits = /^\D*(\d\D*){10}$/;
    return containsTenDigits.test(value);
  };

  /**** END EVENT HANDLING & UTILITY METHODS ****/

  return (
    <Modal
      size="lg"
      backdrop="static"
      container={container as any}
      dialogClassName="sk-backorderModal"
      onHide={() => null}
      show={show}
    >
      <Modal.Header>
        <Modal.Title>{poType} Purchase Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <DragDropContext onBeforeDragStart={onBeforeDragStart} onDragEnd={onDragEnd}>
            <Col xs={8} style={{ paddingRight: '0' }}>
              <Tab.Container activeKey={`po-${activePO}`} id="sk-poNavigator" onSelect={onPOTabSelect}>
                <Row className="clearfix sk-poNavigator">
                  <Col xs={2} className="sk-poSelector">
                    <div className="sk-poSelectorItems">
                      <Nav variant="pills" className="flex-column">
                        {order.purchaseOrders.map((purchaseOrder, i) => (
                          <Nav.Item key={`po-${i}`}>
                            <Nav.Link eventKey={`po-${i}`}>PO</Nav.Link>
                          </Nav.Item>
                        ))}
                      </Nav>
                    </div>
                  </Col>
                  <Col xs={10} className="sk-poContainer">
                    <Tab.Content>
                      {order.purchaseOrders.map((purchaseOrder, poIndex) => {
                        let suggestedVendorWarningMessage = false;
                        return (
                          <Tab.Pane key={`po-${poIndex}`} eventKey={`po-${poIndex}`}>
                            <Droppable droppableId={`po-${poIndex}`}>
                              {(providedDroppable, snapshotDroppable) => (
                                <div
                                  ref={providedDroppable.innerRef}
                                  className="sk-poContents"
                                  style={{
                                    backgroundColor: snapshotDroppable.isDraggingOver ? 'lightyellow' : '#f5f5f5',
                                    transition: 'background-color 0.2s ease',
                                  }}
                                  {...providedDroppable.droppableProps}
                                >
                                  <div
                                    className="sk-poInformation"
                                    style={{ height: showServiceOrBodyOption ? '210px' : undefined }}
                                  >
                                    <div className="sk-poInformationLegend">Purchase Order Details</div>
                                    <div className="sk-poDetailsContainer">
                                      <div id="po-details-control">
                                        <Tabs defaultActiveKey="1">
                                          <Tab eventKey="1" title="Required" tabClassName="po-details-control">
                                            <div className="sk-poDetailsInputFlexContainer">
                                              <div className="sk-poDetailsInput">
                                                <Label>Suggested Vendor</Label>
                                                <select
                                                  disabled={order.purchaseOrders[poIndex].poDetail.vendorSearch !== ''}
                                                  onChange={evt =>
                                                    updatePOField(poIndex, 'suggestedVendor', evt.target.value)
                                                  }
                                                  value={purchaseOrder.poDetail.suggestedVendor}
                                                  style={{
                                                    backgroundColor:
                                                      order.purchaseOrders[poIndex].poDetail.vendorSearch !== ''
                                                        ? '#EBEBE4'
                                                        : undefined,
                                                  }}
                                                >
                                                  <option value={blank}>&nbsp;</option>
                                                  {order.purchaseOrders[poIndex].parts.length > 0 &&
                                                    renderedDerivedVendorSuggestions[poIndex]}
                                                  {vendorSuggestions['ATB13MISC'].length > 0 && (
                                                    <option value="mv-separator" disabled={true}>
                                                      ---------- MISC VENDORS ----------
                                                    </option>
                                                  )}
                                                  {vendorSuggestions['ATB13MISC'].map((miscVendor, mvIndex) => (
                                                    <option key={`mv${mvIndex}`} value={miscVendor.num}>
                                                      {miscVendor.num}: {miscVendor.name}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                              <div className="sk-poDetailsInput">
                                                <Label>Dealer PO</Label>
                                                <input
                                                  type="text"
                                                  onChange={evt => updatePOField(poIndex, 'dealerPO', evt.target.value)}
                                                  value={purchaseOrder.poDetail.dealerPO}
                                                />
                                              </div>
                                              <div className="sk-poDetailsInput">
                                                <Label>Vendor Search</Label>
                                                <VendorLookup
                                                  disabled={isProcessing}
                                                  id={`vendorSearchLookup${poIndex}`}
                                                  onVendorLoad={(vendorID: string) =>
                                                    updatePOField(poIndex, 'vendorSearch', vendorID)
                                                  }
                                                  overflowBreakout={true}
                                                  placeholder=""
                                                  showAdditionalNames={model.sysParams.showVendorName23}
                                                  showVendorName={true}
                                                  style={null}
                                                  ref={ref => {
                                                    vendorSearchLookupRefs[poIndex] = ref as VendorLookup;
                                                  }}
                                                />
                                                <span className="sk-shipInOverrideNote">
                                                  Overrides Suggested Vendor If Set
                                                </span>
                                              </div>
                                              <div className="sk-poDetailsInput">
                                                <Label>Ship In</Label>
                                                <select
                                                  onChange={evt => updatePOField(poIndex, 'shipIn', evt.target.value)}
                                                  value={purchaseOrder.poDetail.shipIn}
                                                >
                                                  <option value={blank}>&nbsp;</option>
                                                  {model.sysParams.backorderShipViaCodes.map(x => (
                                                    <option key={x.code} value={x.code}>
                                                      {x.code} - {x.desc}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            </div>
                                            {showServiceOrBodyOption && (
                                              <div className="sk-poDetailsInput">
                                                <Label>Service / Body</Label>
                                                <select
                                                  onChange={evt => {
                                                    updatePOField(
                                                      poIndex,
                                                      'subletType',
                                                      evt.target.value as IPODetail['subletType']
                                                    );
                                                  }}
                                                  value={purchaseOrder.poDetail.subletType}
                                                >
                                                  <option value="S">S - Service</option>
                                                  <option value="B">B - Body</option>
                                                </select>
                                              </div>
                                            )}
                                          </Tab>
                                          <Tab eventKey="2" title="Optional">
                                            <div className="sk-poDetailsInputFlexContainer">
                                              <div className="sk-poDetailsInput">
                                                <Label>Freight On Board</Label>
                                                <select
                                                  value={purchaseOrder.poDetail.freightOnBoard}
                                                  onChange={evt =>
                                                    updatePOField(poIndex, 'freightOnBoard', evt.target.value)
                                                  }
                                                >
                                                  <option value={blank}>&nbsp;</option>
                                                  <option value="O">O - Origin</option>
                                                  <option value="D">D - Destination</option>
                                                </select>
                                              </div>
                                              <div className="sk-poDetailsInput">
                                                <Label>Freight Payment</Label>
                                                <select
                                                  value={purchaseOrder.poDetail.freightPayment}
                                                  onChange={evt =>
                                                    updatePOField(poIndex, 'freightPayment', evt.target.value)
                                                  }
                                                >
                                                  <option value={blank}>&nbsp;</option>
                                                  <option value="P">P - Pre-paid</option>
                                                  <option value="C">C - Collect</option>
                                                </select>
                                              </div>
                                              <div className="sk-poDetailsInput sk-poInternalComments">
                                                <Label>Internal PO Comments</Label>
                                                <input
                                                  type="text"
                                                  maxLength={50}
                                                  onChange={evt =>
                                                    updatePOField(
                                                      poIndex,
                                                      'dealershipInternalComments',
                                                      evt.target.value
                                                    )
                                                  }
                                                  value={purchaseOrder.poDetail.dealershipInternalComments}
                                                />
                                              </div>
                                            </div>
                                          </Tab>
                                          <Tab eventKey="3" title="Misc Vendor Details">
                                            {isMiscVendorSelected() ? (
                                              <div className="sk-poDetailsInputFlexContainer">
                                                <div className="sk-poDetailsInput">
                                                  <Label>Vendor Name</Label>
                                                  <input
                                                    type="text"
                                                    value={purchaseOrder.poDetail.miscVendorName}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'miscVendorName', evt.target.value)
                                                    }
                                                  />
                                                </div>
                                                <div className="sk-poDetailsInput">
                                                  <Label>Address</Label>
                                                  <input
                                                    type="text"
                                                    value={purchaseOrder.poDetail.miscAddress}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'miscAddress', evt.target.value)
                                                    }
                                                  />
                                                </div>
                                                <div className="sk-poDetailsInput">
                                                  <Label>Attention</Label>
                                                  <input
                                                    type="text"
                                                    value={purchaseOrder.poDetail.miscAttention}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'miscAttention', evt.target.value)
                                                    }
                                                  />
                                                </div>
                                                <div className="sk-poDetailsInput">
                                                  <Label>City, State Zip</Label>
                                                  <input
                                                    type="text"
                                                    value={purchaseOrder.poDetail.miscCityStateZip}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'miscCityStateZip', evt.target.value)
                                                    }
                                                  />
                                                </div>
                                                <div className="sk-poDetailsInput">
                                                  <Label>Phone</Label>
                                                  <input
                                                    type="text"
                                                    value={purchaseOrder.poDetail.miscPhone}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'miscPhone', evt.target.value)
                                                    }
                                                    onBlur={evt => {
                                                      if (!validatePhoneField(evt.target.value)) {
                                                        toastr.error(
                                                          'Error',
                                                          `PO ${poIndex + 1}: Misc vendor phone requires 10 digits.`,
                                                          { toastId: `PO${poIndex}MiscVendorPhone` }
                                                        );
                                                      }
                                                    }}
                                                  />
                                                </div>
                                                <div className="sk-poDetailsInput">&nbsp;</div>
                                              </div>
                                            ) : (
                                              <div className="sk-miscVendorNotSelected">
                                                Miscellaneous Vendor Not Selected
                                              </div>
                                            )}
                                          </Tab>
                                          <Tab eventKey="4" title="Direct Ship To">
                                            <div className="sk-poDetailsInputFlexContainer sk-poDirectShipToFirstRow">
                                              <div className="sk-poDetailsShipToInput">
                                                <input
                                                  type="radio"
                                                  id={`shiptodealership${poIndex}`}
                                                  name={`directshipto${poIndex}`}
                                                  value="dealership"
                                                  checked={purchaseOrder.poDetail.directShipType === 'D'}
                                                  onChange={evt =>
                                                    evt.target.checked && updatePOField(poIndex, 'directShipType', 'D')
                                                  }
                                                />
                                                <label htmlFor={`shiptodealership${poIndex}`}>
                                                  <Label>Dealership</Label>
                                                </label>
                                                <br />
                                                <input type="text" disabled={true} placeholder="No Override" />
                                              </div>

                                              <div className="sk-poDetailsShipToInput">
                                                <input
                                                  type="radio"
                                                  id={`shiptocustomer${poIndex}`}
                                                  name={`directshipto${poIndex}`}
                                                  value="customer"
                                                  checked={purchaseOrder.poDetail.directShipType === 'C'}
                                                  onChange={evt =>
                                                    evt.target.checked && updatePOField(poIndex, 'directShipType', 'C')
                                                  }
                                                />
                                                <label htmlFor={`shiptocustomer${poIndex}`}>
                                                  <Label>Customer #</Label>
                                                </label>
                                                <br />
                                                <CustomerLookup
                                                  activeId={null}
                                                  disabled={
                                                    purchaseOrder.poDetail.directShipType !== 'C' || isProcessing
                                                  }
                                                  hideInactiveAccounts={model.sysParams.hideInactiveAccounts}
                                                  id={`directShipCustomerLookup${poIndex}`}
                                                  onCustomerLoad={(customerID: string) =>
                                                    updatePOField(poIndex, 'directShipCustomerNum', customerID)
                                                  }
                                                  onEnterKeyPressed={() => {
                                                    /* */
                                                  }}
                                                  onPickListLoad={() => {
                                                    console.error(
                                                      'Legacy pick list loads not supported in backshipping modal.'
                                                    );
                                                  }}
                                                  overflowBreakout={true}
                                                  placeholder=""
                                                  showAdditionalNames={model.sysParams.showName23}
                                                  skipActiveIDCheck={true}
                                                  style={null}
                                                  ref={ref => {
                                                    directShipCustomerLookupRefs[poIndex] = ref as CustomerLookup;
                                                  }}
                                                />
                                              </div>
                                              <div className="sk-poDetailsShipToInput">
                                                <input
                                                  type="radio"
                                                  id={`shiptovendor${poIndex}`}
                                                  name={`directshipto${poIndex}`}
                                                  value="vendor"
                                                  checked={purchaseOrder.poDetail.directShipType === 'V'}
                                                  onChange={evt =>
                                                    evt.target.checked && updatePOField(poIndex, 'directShipType', 'V')
                                                  }
                                                />
                                                <label htmlFor={`shiptovendor${poIndex}`}>
                                                  <Label>Vendor #</Label>
                                                </label>
                                                <br />
                                                <VendorLookup
                                                  disabled={
                                                    purchaseOrder.poDetail.directShipType !== 'V' || isProcessing
                                                  }
                                                  id={`directShipVendorLookup${poIndex}`}
                                                  onVendorLoad={(vendorID: string) =>
                                                    updatePOField(poIndex, 'directShipVendorNum', vendorID)
                                                  }
                                                  overflowBreakout={true}
                                                  placeholder=""
                                                  showAdditionalNames={model.sysParams.showVendorName23}
                                                  style={null}
                                                  ref={ref => {
                                                    directShipVendorLookupRefs[poIndex] = ref as VendorLookup;
                                                  }}
                                                />
                                              </div>
                                            </div>
                                            <hr />
                                            <div className="sk-poDetailsInputFlexContainer sk-poDirectShipToOtherRows">
                                              <div className="sk-poDetailsShipToInput">
                                                <input
                                                  type="radio"
                                                  id={`shiptoother${poIndex}`}
                                                  name={`directshipto${poIndex}`}
                                                  value="other"
                                                  checked={purchaseOrder.poDetail.directShipType === 'O'}
                                                  onChange={evt =>
                                                    evt.target.checked && updatePOField(poIndex, 'directShipType', 'O')
                                                  }
                                                />
                                                <label htmlFor={`shiptoother${poIndex}`}>
                                                  <Label>
                                                    <span className="sk-poDirectShipToOtherLabel">Other</span>
                                                  </Label>
                                                </label>
                                              </div>
                                              <div className="sk-poDetailsShipToInput">
                                                <Label>Name</Label>
                                                <input
                                                  type="text"
                                                  disabled={purchaseOrder.poDetail.directShipType !== 'O'}
                                                  value={purchaseOrder.poDetail.directShipOtherName}
                                                  onChange={evt =>
                                                    updatePOField(poIndex, 'directShipOtherName', evt.target.value)
                                                  }
                                                />
                                              </div>
                                              <div className="sk-poDetailsShipToInput">
                                                <Label>Attention</Label>
                                                <input
                                                  type="text"
                                                  disabled={purchaseOrder.poDetail.directShipType !== 'O'}
                                                  value={purchaseOrder.poDetail.directShipOtherAttention}
                                                  onChange={evt =>
                                                    updatePOField(poIndex, 'directShipOtherAttention', evt.target.value)
                                                  }
                                                />
                                              </div>
                                              <div className="sk-poDetailsShipToInput">
                                                <Label>Address</Label>
                                                <input
                                                  type="text"
                                                  disabled={purchaseOrder.poDetail.directShipType !== 'O'}
                                                  value={purchaseOrder.poDetail.directShipOtherAddress}
                                                  onChange={evt =>
                                                    updatePOField(poIndex, 'directShipOtherAddress', evt.target.value)
                                                  }
                                                />
                                              </div>
                                              <div className="sk-poDetailsShipToInput">
                                                <Label>City, State Zip</Label>
                                                <input
                                                  type="text"
                                                  disabled={purchaseOrder.poDetail.directShipType !== 'O'}
                                                  value={purchaseOrder.poDetail.directShipOtherCityStateZip}
                                                  onChange={evt =>
                                                    updatePOField(
                                                      poIndex,
                                                      'directShipOtherCityStateZip',
                                                      evt.target.value
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div className="sk-poDetailsShipToInput">
                                                <Label>Phone</Label>
                                                <input
                                                  type="text"
                                                  disabled={purchaseOrder.poDetail.directShipType !== 'O'}
                                                  value={purchaseOrder.poDetail.directShipOtherPhone}
                                                  onChange={evt =>
                                                    updatePOField(poIndex, 'directShipOtherPhone', evt.target.value)
                                                  }
                                                  onBlur={evt => {
                                                    if (!validatePhoneField(evt.target.value)) {
                                                      toastr.error(
                                                        'Error',
                                                        `PO ${
                                                          poIndex + 1
                                                        }: Direct ship to "other" phone requires 10 digits.`,
                                                        { toastId: `PO${poIndex}DirectShipPhone` }
                                                      );
                                                    }
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          </Tab>
                                        </Tabs>
                                      </div>
                                    </div>
                                  </div>

                                  {purchaseOrder.parts.length === 0 ? (
                                    <div className="sk-poPartsEmpty">No parts attached to this PO.</div>
                                  ) : (
                                    <>
                                      <div className="sk-poParts">
                                        <table className="sk-poPartLines">
                                          <thead>
                                            <tr>
                                              <td title="Item" colSpan={4}>
                                                Item
                                              </td>
                                              <td title="Stock Class">Stkcls</td>
                                              <td title="Requested Quantity">Req</td>
                                              <td>&nbsp;</td>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {purchaseOrder.parts.map((part, partLineIndex) => {
                                              const isPoComment = 'poComment' in part;
                                              const partDetails = parts[part.partIndex] as ICandidatePartDetail;
                                              const suggestedVendor = purchaseOrder.poDetail.suggestedVendor;
                                              let suggestedVendorStockClassMistmatch = false;

                                              // Suggested vendor could be a miscellaneous vendor, which supports all stock classes
                                              if (
                                                !isPoComment &&
                                                purchaseOrder.poDetail.vendorSearch.trim() === '' &&
                                                derivedVendorSuggestions[poIndex] &&
                                                suggestedVendor in derivedVendorSuggestions[poIndex]
                                              ) {
                                                if (
                                                  !derivedVendorSuggestions[poIndex][suggestedVendor].stockClasses.has(
                                                    partDetails.stockClass
                                                  )
                                                ) {
                                                  suggestedVendorStockClassMistmatch = true;
                                                  suggestedVendorWarningMessage = true;
                                                }
                                              }

                                              return (
                                                <Draggable
                                                  draggableId={part.partIndex}
                                                  key={part.partIndex}
                                                  index={partLineIndex}
                                                >
                                                  {(providedDraggable, snapshotDraggable) => {
                                                    let child = (
                                                      <tr
                                                        className={classNames({
                                                          'sk-noBgHighlight': dragState.isDragging,
                                                          'sk-suggestedVendorStockClassMismatch':
                                                            suggestedVendorStockClassMistmatch,
                                                        })}
                                                        {...providedDraggable.draggableProps}
                                                      >
                                                        {isPoComment ? (
                                                          part.poCommentIsEditing ? (
                                                            <td
                                                              colSpan={6}
                                                              ref={providedDraggable.innerRef}
                                                              {...providedDraggable.dragHandleProps}
                                                            >
                                                              <input
                                                                defaultValue={part.poComment}
                                                                onBlur={() =>
                                                                  updatePartLineComment({
                                                                    partLineIndex,
                                                                    poIndex: activePO,
                                                                  })
                                                                }
                                                                onKeyPress={evt => {
                                                                  if (evt.key === 'Enter') {
                                                                    updatePartLineComment({
                                                                      partLineIndex,
                                                                      poIndex: activePO,
                                                                    });
                                                                  }
                                                                }}
                                                                ref={poCommentEditInputRef}
                                                                style={{ lineHeight: 1, width: '100%' }}
                                                                type="text"
                                                              />
                                                            </td>
                                                          ) : (
                                                            <td
                                                              colSpan={6}
                                                              className="sk-poCommentLine"
                                                              onDoubleClick={() => {
                                                                if (isProcessing) {
                                                                  return;
                                                                }
                                                                setCommentIsEditing({
                                                                  partLineIndex,
                                                                  poIndex: activePO,
                                                                });
                                                              }}
                                                            >
                                                              <div
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.dragHandleProps}
                                                                className="sk-clipper"
                                                              >
                                                                {part.poComment}
                                                              </div>
                                                            </td>
                                                          )
                                                        ) : (
                                                          <>
                                                            <td
                                                              title={`${partDetails.partNum} - ${partDetails.desc}`}
                                                              colSpan={4}
                                                            >
                                                              <div
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.dragHandleProps}
                                                              >
                                                                <div className="sk-clipper">
                                                                  {partDetails.partNum}
                                                                  {partDetails.subletComment && (
                                                                    <div className="sk-clipper sk-poAttachedSubletCommentLine">
                                                                      {partDetails.subletComment}
                                                                    </div>
                                                                  )}
                                                                </div>
                                                              </div>
                                                            </td>
                                                            <td title={partDetails.stockClass}>
                                                              <div className="sk-clipper">{partDetails.stockClass}</div>
                                                            </td>
                                                            <td>
                                                              <div className="sk-clipper">{partDetails.req}</div>
                                                            </td>
                                                          </>
                                                        )}
                                                        <td>
                                                          <i
                                                            className="zmdi zmdi-delete zmdi-hc-lg sk-poRemoveItemIcon"
                                                            onClick={() => removePartLine({ partLineIndex, poIndex })}
                                                          />
                                                        </td>
                                                      </tr>
                                                    );

                                                    if (snapshotDraggable.isDragging) {
                                                      const content = isPoComment ? (
                                                        part.poComment
                                                      ) : (
                                                        <>
                                                          {partDetails.partNum}
                                                          <br />
                                                          <span style={{ fontSize: '10px', fontWeight: 'normal' }}>
                                                            {partDetails.stockClass &&
                                                              `(STK:${partDetails.stockClass}) `}
                                                            {partDetails.desc}
                                                            {partDetails.subletComment && (
                                                              <>
                                                                <br />
                                                                {partDetails.subletComment}
                                                              </>
                                                            )}
                                                          </span>
                                                        </>
                                                      );

                                                      child = (
                                                        <div
                                                          ref={providedDraggable.innerRef}
                                                          {...providedDraggable.draggableProps}
                                                          {...providedDraggable.dragHandleProps}
                                                          style={getDraggableStyle(
                                                            providedDraggable.draggableProps.style,
                                                            snapshotDraggable,
                                                            order.purchaseOrders[activePO].parts.length === 0
                                                          )}
                                                        >
                                                          {/* Inline styles required in all parts of this DOM node tree since it will be rendered in a portal. */}
                                                          <div
                                                            style={{
                                                              backgroundColor: '#e2f1fd',
                                                              border: '1px solid #2196f3',
                                                              fontStyle: isPoComment ? 'italic' : 'normal',
                                                              fontWeight: isPoComment ? 'normal' : 'bold',
                                                              lineHeight: '1.1',
                                                              overflowX: 'hidden',
                                                              padding: '5px',
                                                              textOverflow: 'ellipsis',
                                                              whiteSpace: 'nowrap',
                                                              width: '235px',
                                                            }}
                                                          >
                                                            {content}
                                                          </div>
                                                        </div>
                                                      );

                                                      return ReactDOM.createPortal(child, portal);
                                                    } else {
                                                      return child;
                                                    }
                                                  }}
                                                </Draggable>
                                              );
                                            })}
                                            <tr>
                                              <td colSpan={7}>{providedDroppable.placeholder}</td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                      {suggestedVendorWarningMessage && (
                                        <div className="sk-suggestedVendorWarningMessage">
                                          The suggested vendor chosen does not support all stock classes on this PO.
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          </Tab.Pane>
                        );
                      })}
                    </Tab.Content>
                  </Col>
                </Row>
              </Tab.Container>
            </Col>
            <Col xs={4} style={{ paddingLeft: '10px' }}>
              {!isSMM3 && (
                <div>
                  <FormCheck inline={true}>
                    <FormCheck.Input
                      type="checkbox"
                      id="sk-show-all-parts"
                      className="sk-useMaterialInput ml-1"
                      onChange={(evt: any) => handleShowAllParts(evt.target.checked)}
                      checked={showAllParts}
                    />
                    <FormCheck.Label htmlFor="sk-show-all-parts">Show Comments</FormCheck.Label>
                  </FormCheck>
                </div>
              )}
              <Droppable droppableId="partList">
                {(providedDroppable, snapshotDroppable) => (
                  <div
                    ref={providedDroppable.innerRef}
                    className="sk-partSelector"
                    style={{
                      backgroundColor: snapshotDroppable.isDraggingOver ? 'lightyellow' : '#f5f5f5',
                      transition: 'background-color 0.2s ease',
                    }}
                    {...providedDroppable.droppableProps}
                  >
                    {visibleCandidatePartListLength === 0 ? (
                      <div style={{ textAlign: 'center' }}>Empty</div>
                    ) : (
                      <>
                        {(() => {
                          /* react-beautiful-dnd requires sequential indexes, but in our candidate list scheme,
                             some parts might be skipped (sufficient qty with show parts unchecked).
                             Proper transposition (of the candiate list) occurs in onDragEnd().
                          */
                          let renderIndex = 0;
                          return candidatePartList.map(candidatePartIndex => {
                            const partDetails = parts[candidatePartIndex] as ICandidatePartDetail;

                            if (partDetails.sufficientQty && !showAllParts) {
                              return null;
                            }

                            return (
                              <Draggable
                                draggableId={candidatePartIndex}
                                index={renderIndex++}
                                key={`draggable-${candidatePartIndex}`}
                              >
                                {(providedDraggable, snapshotDraggable) => {
                                  let backgroundColor, border, color;

                                  // Blue parts = candidate order parts
                                  // Orange parts = parts with sufficient qty on hand to meet demand ("Show All" feature)

                                  // Is this item selected in a multi-part drag?
                                  if (partSelectionToggles[candidatePartIndex]) {
                                    // If we're currently dragging the multi-part set and NOT the primary drag item (gray out).
                                    if (dragState.isDragging && dragState.id !== candidatePartIndex) {
                                      backgroundColor = '#f5f5f5';
                                      color = '#c2c2c2';
                                      border = '1px solid #c2c2c2';
                                      // Otherwise indicate we are part of a multi-part drag set (darker blue).
                                    } else {
                                      backgroundColor = '#b2dbfb';
                                      border = '1px solid #2196f3';
                                    }
                                    // Not selected in multi-part drag (standard light blue / light orange).
                                  } else {
                                    if (partDetails.partNum === 'LINEITEM_COMMENT') {
                                      backgroundColor = '#f9ecf6';
                                      border = '1px solid #a03588';
                                    } else if (partDetails.sufficientQty) {
                                      backgroundColor = '#ffe8cc';
                                      border = '1px solid #ff8c00';
                                    } else {
                                      backgroundColor = '#e2f1fd';
                                      border = '1px solid #2196f3';
                                    }
                                  }

                                  const child = (
                                    <div
                                      className="sk-partSelectorItem"
                                      onClick={event => onPartClick(event, candidatePartIndex)}
                                      ref={providedDraggable.innerRef}
                                      {...providedDraggable.draggableProps}
                                      {...providedDraggable.dragHandleProps}
                                      style={getDraggableStyle(
                                        providedDraggable.draggableProps.style,
                                        snapshotDraggable,
                                        order.purchaseOrders[activePO].parts.length === 0
                                      )}
                                    >
                                      {/* Inline styles required in all parts of this DOM node tree since it will be rendered in a portal. */}
                                      <div
                                        style={{
                                          backgroundColor,
                                          border,
                                          color,
                                          fontWeight: 'bold',
                                          lineHeight: '1.1',
                                          overflowX: 'hidden',
                                          padding: '5px',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {partDetails.partNum === 'LINEITEM_COMMENT'
                                          ? 'Memo / Comment'
                                          : partDetails.partNum}
                                        <br />
                                        <span style={{ fontSize: '10px', fontWeight: 'normal' }}>
                                          {partDetails.stockClass && `(STK:${partDetails.stockClass}) `}
                                          {partDetails.desc}
                                          {partDetails.subletComment && (
                                            <>
                                              <br />
                                              {partDetails.subletComment}
                                            </>
                                          )}
                                        </span>
                                        {snapshotDraggable.isDragging && selectedPartCount > 1 && (
                                          <div
                                            style={{
                                              alignItems: 'center',
                                              backgroundColor: '#2196f3',
                                              borderRadius: '50%',
                                              color: 'white',
                                              display: 'flex',
                                              fontSize: '12px',
                                              height: '28px',
                                              justifyContent: 'center',
                                              position: 'absolute',
                                              right: '-13px',
                                              top: '-13px',
                                              width: '28px',
                                            }}
                                          >
                                            {selectedPartCount}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );

                                  if (snapshotDraggable.isDragging) {
                                    return ReactDOM.createPortal(child, portal);
                                  } else {
                                    return child;
                                  }
                                }}
                              </Draggable>
                            );
                          });
                        })()}
                        {providedDroppable.placeholder}
                      </>
                    )}
                  </div>
                )}
              </Droppable>
              {visibleCandidatePartListLength !== 0 && (
                <div className="sk-partListActions">
                  <Button size="sm" onClick={addAllPartsToPO} variant="secondary">
                    ADD REMAINING
                  </Button>
                  <Button size="sm" onClick={clearPartSelection} variant="secondary">
                    CLEAR SELECTION
                  </Button>
                </div>
              )}
              <div className="sk-poComment">
                <input
                  type="text"
                  placeholder="Comment (Press ENTER to add to PO)"
                  ref={poCommentInputRef}
                  onKeyPress={evt => {
                    if (evt.key === 'Enter' && poCommentInputRef.current!.value.trim().length) {
                      addPOComment();
                    }
                  }}
                />
              </div>
            </Col>
          </DragDropContext>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <div>
            <Dropdown drop="up" as={ButtonGroup}>
              <Button variant="warning" id="split-button-dropup" onClick={addPO}>
                ADD PO
              </Button>
              <Dropdown.Toggle split={true} variant="warning" id="split-button-dropup" />
              <Dropdown.Menu>
                <Dropdown.Item onClick={deletePO}>Delete Selected PO</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div>
            <Button disabled={isProcessing} onClick={hide} variant="secondary">
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => {
                if (!isProcessing) {
                  if (validateOrder()) {
                    setIsProcessing(true);
                    process();
                  }
                }
              }}
            >
              {isProcessing ? (
                <>
                  <ClipLoader size={13} color="white" /> PROCESSING
                </>
              ) : (
                'PROCESS'
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SubletUnpricedPOModal;
